import { Button, Dialog, DialogContent, DialogTitle, Stack } from '@evaneos/b2b-ui';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { useTrackEventAmplitude } from '../../../shared/analytics';
import { OverloadedReason, WorkloadStatus } from '../types';
import { useGetLoggedAgent } from '../../agent/hooks';
import { useSetAgentWorkloadMutation } from '../mutations';
import { logModalOpened, logReasonSelected } from '../logging';

interface OverloadedReasonDialogProps {
    isOpen: boolean;
    onClose?: () => void;
    onReasonSelected?: (reason: OverloadedReason) => void;
}

export const AgentOverloadedReasonModal: React.FC<OverloadedReasonDialogProps> = ({
    isOpen,
    onClose = () => {},
    onReasonSelected,
}) => {
    const { mutate: setAgentWorkload } = useSetAgentWorkloadMutation();
    const { trackEvent } = useTrackEventAmplitude();

    const agent = useGetLoggedAgent();

    useEffect(() => {
        if (isOpen) {
            logModalOpened(
                'AgentOverloadedReasonModal',
                {
                    hasOnReasonSelected: !!onReasonSelected,
                },
                agent,
            );
        }
    }, [isOpen, onReasonSelected, agent]);

    const handleReasonSelected = (reason: OverloadedReason) => {
        if (onReasonSelected) {
            onReasonSelected(reason);
        } else {
            changeAgentWorkloadStatus(reason);
        }
    };

    function changeAgentWorkloadStatus(overloadedReason: OverloadedReason): void {
        logReasonSelected(
            'AgentOverloadedReasonModal',
            {
                status: WorkloadStatus.Overloaded,
                reason: overloadedReason,
            },
            agent,
        );

        setAgentWorkload({
            status: WorkloadStatus.Overloaded,
            overloadedReason: overloadedReason,
            lastUpdatedAt: new Date(),
        });

        trackEvent((ampli, defaultProperties) => {
            ampli.workloadStatusClicked({
                ...defaultProperties,
                agent_workload_level: WorkloadStatus.Overloaded,
            });
        });

        onClose();
    }

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <p style={{ fontSize: `2.2rem`, lineHeight: `3rem` }}>
                    <FormattedMessage id="agent_settings.workload_status.overloaded_reason_modal.title" />
                </p>
                <p
                    style={{
                        fontSize: `1.6rem`,
                        lineHeight: `2rem`,
                        color: `var(--ink-light)`,
                    }}
                >
                    <FormattedMessage id="agent_settings.workload_status.overloaded_reason_modal.sub_title" />
                </p>
            </DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={1}>
                    {Object.values(OverloadedReason).map((reason) => (
                        <Button
                            key={reason}
                            variant="outlined"
                            onClick={() => handleReasonSelected(reason)}
                        >
                            <FormattedMessage
                                id={`agent_settings.workload_status.overloaded_reasons.${reason}`}
                            />
                        </Button>
                    ))}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};
